import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ApiError } from '../../lib/api';
import { parseDriveFolderUrl } from '../../lib/parseDriveFolderUrl';
import { extractVideoThumbnail } from '../../utils/extractVideoThumbnail';
import type {
  AdditionalAd,
  CreativeAsset,
  MetaAdDraft,
  MetaAdDraftInput,
  MetaAdDriveSource,
} from '../../types/metaAdDraft';
import { listDriveFolder } from '../drive/api';
import {
  fetchDriveFileBlob,
  getAssetUploadUrl,
  getDraft,
  patchDraft,
  putFileWithProgress,
} from './api';
import {
  FOLDER_LABEL,
  FOLDER_ORDER,
  planDriveCreative,
  type DriveCreativePlan,
  type PlannedAd,
  type PlannedAsset,
  type PlannerFolderInput,
} from './driveCreative';

// Build Campaign Phase 2 — the in-builder "Pull creative from Drive" action.
// Lists the rollout's three Drive folders (live), runs the pure planner
// (classify → pair → cap), then for each kept ad streams the Drive bytes →
// Blob → the EXISTING upload path (getAssetUploadUrl + putFileWithProgress, +
// extractVideoThumbnail for video) → fills the primary ad + additionalAds and
// PATCHes the draft. Singles go in BOTH slots so they run on stories too.
//
// Upload failures flag that asset and continue the batch (seed-and-flag). All
// other I/O lives here; the planning is pure (driveCreative.ts).

type Props = {
  draftId: string;
  accountId: string;
  driveSource: MetaAdDriveSource;
  currentInput: MetaAdDraftInput;
  version: number;
  onApplied: (draft: MetaAdDraft) => void;
  onClose: () => void;
};

type RunProgress = {
  total: number;
  current: number; // 1-based index of the unit being processed
  label: string; // current asset filename
  pct: number; // current file upload %
};

const EMPTY_ASSET: CreativeAsset = { type: null, url: null, path: null, thumbnailUrl: null, thumbnailPath: null };

export function PullCreativeModal({
  draftId,
  accountId,
  driveSource,
  currentInput,
  version,
  onApplied,
  onClose,
}: Props) {
  // ---------- plan (list folders live + run the pure planner) ----------
  const planQuery = useQuery({
    queryKey: ['pull-creative-plan', draftId],
    queryFn: async (): Promise<DriveCreativePlan> => {
      const listWarnings: string[] = [];
      const inputs: PlannerFolderInput[] = [];
      for (const key of FOLDER_ORDER) {
        const url = driveSource.folders[key];
        if (!url) continue;
        const folderId = parseDriveFolderUrl(url);
        if (!folderId) {
          listWarnings.push(`The ${FOLDER_LABEL[key]} folder link couldn't be read (invalid URL).`);
          continue;
        }
        try {
          const listing = await listDriveFolder(folderId);
          inputs.push({
            key,
            files: listing.files.map((f) => ({ id: f.id, name: f.name, mimeType: f.mimeType })),
            labels: driveSource.labels[key],
          });
        } catch (e) {
          const msg = e instanceof ApiError ? e.message : 'unavailable';
          listWarnings.push(`Couldn't read the ${FOLDER_LABEL[key]} folder: ${msg}`);
        }
      }
      const plan = planDriveCreative(inputs);
      return { ...plan, warnings: [...listWarnings, ...plan.warnings] };
    },
  });

  // ---------- run (upload + patch) ----------
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [runWarnings, setRunWarnings] = useState<string[]>([]);
  const [doneSummary, setDoneSummary] = useState<{ added: number; failed: number } | null>(null);

  const run = useMutation({
    mutationFn: async () => {
      const plan = planQuery.data;
      if (!plan || plan.ads.length === 0) return;
      const warnings: string[] = [];
      const built: { unit: PlannedAd; feed: CreativeAsset; story: CreativeAsset }[] = [];

      for (let i = 0; i < plan.ads.length; i += 1) {
        const unit = plan.ads[i]!;
        try {
          const setPct = (pct: number) =>
            setProgress({ total: plan.ads.length, current: i + 1, label: unit.feed.fileName, pct });
          setPct(0);
          const feed = await uploadAsset(accountId, unit.feed, warnings, setPct);
          // Pair → upload the DISTINCT story asset (drives per-placement customization).
          // Single → leave the story slot EMPTY: the publish mapper sends a plain
          // primary creative that Meta delivers across ALL placements. (Duplicating the
          // one asset into both slots makes the same hash the default AND the story
          // rule → Meta 400 "All ad asset values should be unique".)
          const story = unit.story
            ? await uploadAsset(accountId, unit.story, warnings, (p) =>
                setProgress({ total: plan.ads.length, current: i + 1, label: unit.story!.fileName, pct: p }),
              )
            : EMPTY_ASSET;
          built.push({ unit, feed, story });
        } catch (e) {
          // The message is the SPECIFIC reason — the server names the Drive download
          // failure (shortcut / abusive / not found / HTTP code) or the upload error.
          const msg = e instanceof Error ? e.message : 'failed';
          warnings.push(`"${unit.feed.fileName}" failed — skipped (${msg}).`);
        }
      }

      if (built.length === 0) {
        setRunWarnings(warnings);
        setDoneSummary({ added: 0, failed: plan.ads.length });
        return;
      }

      // Assemble the ads: built[0] → primary (keeps Phase-1 copy/destination/pixel/
      // utm); the rest → additionalAds (copyOverride null = inherit that copy).
      const primary = built[0]!;
      const rest = built.slice(1);
      const nextAds: MetaAdDraftInput['ads'] = {
        ...currentInput.ads,
        // Ad name = the label (the planner already falls back to the filename when an
        // asset has no label). Empty keeps the Phase-1 default (→ campaign name).
        name: primary.unit.name || currentInput.ads.name,
        format: primary.unit.mediaType === 'video' ? 'single_video' : 'single_image',
        creative: {
          ...currentInput.ads.creative,
          assetType: primary.unit.mediaType,
          feedAsset: primary.feed,
          storyAsset: primary.story,
        },
        additionalAds: rest.map<AdditionalAd>((b) => ({
          id: crypto.randomUUID(),
          name: b.unit.name || b.unit.feed.fileName,
          assetType: b.unit.mediaType,
          feedAsset: b.feed,
          storyAsset: b.story,
          copyOverride: null,
          status: 'ACTIVE',
          publishState: null,
        })),
      };

      const nextInput: MetaAdDraftInput = { ...currentInput, ads: nextAds };
      let result;
      try {
        result = await patchDraft(draftId, nextInput, version);
      } catch (e) {
        // Version moved on (a racing autosave) — refetch + retry once.
        if (e instanceof ApiError && e.status === 409 && e.message === 'version_conflict') {
          const fresh = await getDraft(draftId);
          result = await patchDraft(draftId, nextInput, fresh.draft.version);
        } else {
          throw e;
        }
      }

      setRunWarnings(warnings);
      setDoneSummary({ added: built.length, failed: plan.ads.length - built.length });
      onApplied(result.draft);
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !run.isPending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [run.isPending, onClose]);

  const plan = planQuery.data;
  const busy = run.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-lg rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pull-creative-title"
      >
        <div className="space-y-4 p-6">
          <h2 id="pull-creative-title" className="text-lg font-semibold text-slate-900">
            Pull creative from Drive
          </h2>

          {planQuery.isLoading && <CenteredSpinner />}

          {planQuery.error && (
            <p className="text-sm text-red-600">
              {planQuery.error instanceof ApiError
                ? planQuery.error.message
                : "Couldn't read the Drive folders."}
            </p>
          )}

          {/* Review (pre-run) */}
          {plan && !doneSummary && (
            <div className="space-y-4">
              {plan.ads.length > 0 ? (
                <div className="space-y-1">
                  <p className="text-sm text-slate-700">
                    {plan.ads.length} ad{plan.ads.length === 1 ? '' : 's'} will be created from
                    your Drive folders — added to every ad set, sharing the campaign copy. No
                    placeholder is kept.
                  </p>
                  <ul className="max-h-40 list-disc overflow-auto pl-5 text-sm text-slate-600">
                    {plan.ads.map((a, i) => (
                      <li key={`${a.feed.fileId}-${i}`}>
                        {a.name || a.feed.fileName} —{' '}
                        {a.kind === 'pair' ? `${a.mediaType} pair (feed + story)` : `single ${a.mediaType}`}{' '}
                        <span className="text-slate-400">({FOLDER_LABEL[a.folder]})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-slate-700">
                  No creative to add — the placeholder ad stays. See the notes below.
                </p>
              )}

              <WarningList warnings={plan.warnings} />

              {progress && busy && (
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">
                    Uploading asset {progress.current} of {progress.total}: {progress.label}
                  </p>
                  <div className="h-2 w-full overflow-hidden rounded bg-slate-100">
                    <div className="h-full bg-kyck-500 transition-all" style={{ width: `${progress.pct}%` }} />
                  </div>
                </div>
              )}

              {run.error && (
                <p className="text-sm text-red-600">
                  {run.error instanceof ApiError && run.error.message === 'locked'
                    ? 'This draft is published and can no longer be edited.'
                    : "Couldn't apply the creative. Try again."}
                </p>
              )}
            </div>
          )}

          {/* Done */}
          {doneSummary && (
            <div className="space-y-3">
              <p className="text-sm text-slate-800">
                {doneSummary.added > 0
                  ? `Added creative to ${doneSummary.added} ad${doneSummary.added === 1 ? '' : 's'}.`
                  : 'No creative could be added.'}
                {doneSummary.failed > 0 && ` ${doneSummary.failed} skipped.`}
              </p>
              <WarningList warnings={runWarnings} />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            {doneSummary ? (
              <Button onClick={onClose}>Done</Button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                {plan && plan.ads.length > 0 && (
                  <Button onClick={() => run.mutate()} disabled={busy}>
                    {busy
                      ? 'Adding creative…'
                      : `Add creative to ${plan.ads.length} ad${plan.ads.length === 1 ? '' : 's'}`}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function WarningList({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="space-y-1 rounded border border-amber-200 bg-amber-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Heads up</p>
      <ul className="list-disc pl-5 text-sm text-amber-800">
        {warnings.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

// Stream one Drive asset's bytes → Blob → the EXISTING upload path → a CreativeAsset.
// Videos additionally auto-capture + upload a thumbnail (best-effort: a failure flags
// but still seeds the video — Meta auto-generates a thumbnail when omitted).
async function uploadAsset(
  accountId: string,
  asset: PlannedAsset,
  warnings: string[],
  onProgress: (pct: number) => void,
): Promise<CreativeAsset> {
  const { blob, contentType } = await fetchDriveFileBlob(asset.fileId);
  const ct = contentType || (asset.mediaType === 'video' ? 'video/mp4' : 'image/jpeg');
  const fileForUpload = new File([blob], asset.fileName, { type: ct });

  const up = await getAssetUploadUrl({
    accountId,
    contentType: ct,
    originalFilename: asset.fileName,
  });
  await putFileWithProgress(up.uploadUrl, fileForUpload, up.requiredHeaders, onProgress);

  let thumbnailUrl: string | null = null;
  let thumbnailPath: string | null = null;
  if (asset.mediaType === 'video') {
    try {
      const thumbBlob = await extractVideoThumbnail(fileForUpload);
      const thumbFile = new File([thumbBlob], `${asset.fileName}.thumb.jpg`, { type: 'image/jpeg' });
      const t = await getAssetUploadUrl({
        accountId,
        contentType: 'image/jpeg',
        originalFilename: thumbFile.name,
      });
      await putFileWithProgress(t.uploadUrl, thumbFile, t.requiredHeaders);
      thumbnailUrl = t.imageUrl;
      thumbnailPath = t.imagePath;
    } catch {
      warnings.push(
        `Couldn't auto-capture a thumbnail for "${asset.fileName}" — add one in the builder if needed.`,
      );
    }
  }

  return {
    ...EMPTY_ASSET,
    type: asset.mediaType,
    url: up.imageUrl,
    path: up.imagePath,
    thumbnailUrl,
    thumbnailPath,
  };
}
